import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { getSuspendData, setSuspendData, getLessonMode, isRXDAvailable } from '../utils/rxd-api';

export interface RXDState {
	modulePosition: number; // Position within the course (0-based index)
	timestamp: number;
}

export type LessonMode = "normal" | "browse" | "review";

interface RXDStateContextType {
	// State
	isLoading: boolean;
	error: string | null;
	rxdState: RXDState | null;
	lessonMode: LessonMode | null;
	isRXDAvailable: boolean;
	
	// Suspend data actions
	saveSuspendData: () => Promise<void>;
	loadSuspendData: () => Promise<void>;
	loadLessonMode: () => Promise<void>;
	updateModulePosition: (newPosition: number) => Promise<void>;
	updateVideoProgress: (timestamp: number) => Promise<void>;

	
	// Computed values
	getNextModulePosition: () => number;
	isModulePositionAccessible: (position: number) => boolean;
	isModuleCompleted: (position: number) => boolean;
	
	// Lesson mode helpers
	isNormalMode: () => boolean;
	isBrowseMode: () => boolean;
	isReviewMode: () => boolean;
	shouldSaveProgress: () => boolean;
	
	// Utility functions
	clearError: () => void;
	reset: () => void;
	
	// Serialization helpers (for debugging/testing)
	serializeState: (data: RXDState) => string; // Returns CSV format
	deserializeState: (csvString: string) => RXDState | null; // Parses CSV format
}

const RXDStateContext = createContext<RXDStateContextType | undefined>(undefined);

export const useRXDStateContext = () => {
	const context = useContext(RXDStateContext);
	if (context === undefined) {
		throw new Error('useRXDStateContext must be used within a RXDStateProvider');
	}
	return context;
};

interface RXDStateProviderProps {
	children: ReactNode;
	courseId?: string;
}

export const RXDStateProvider: React.FC<RXDStateProviderProps> = ({ children, courseId }) => {
	const [state, setState] = useState<RXDState | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [lessonMode, setLessonMode] = useState<LessonMode | null>(null);
	const [rxdAvailable, setRxdAvailable] = useState(false);

	const serializeState = useCallback((data: RXDState): string => {
		const csvString = `${data.modulePosition}:${data.timestamp}`;
		
		// Check if we're within SCORM 1.2 limit (4000 characters)
		if (csvString.length > 4000) {
			console.warn(`Suspend data string (${csvString.length} chars) exceeds SCORM 1.2 limit of 4000 characters`);
		}
		
		return csvString;
	}, []);

	// Parse suspend data from compact CSV format: modulePosition:timestamp
	const deserializeState = useCallback((csvString: string): RXDState | null => {
		try {
			const parts = csvString.split(':');
			
			if (parts.length === 2) {
				const [modulePositionStr, timestampStr] = parts;
				const modulePosition = parseInt(modulePositionStr, 10);
				const timestamp = parseInt(timestampStr, 10);
				
				if (!isNaN(modulePosition) && !isNaN(timestamp)) {
					return {
						modulePosition,
						timestamp,
					};
				}
			}
			return null;
		} catch (error) {
			console.error('Failed to parse bookmark CSV:', error);
			return null;
		}
	}, []);





	// Get the next accessible module position based on bookmark data
	const getNextModulePosition = useCallback(() => {
		if (!state) return 0;
		return state.modulePosition + 1;
	}, [state]);

	// Check if a specific module position is accessible
	const isModulePositionAccessible = useCallback((position: number) => {
		if (position === 0) return true; // First module is always accessible
		return state !== null && state.modulePosition >= position - 1;
	}, [state]);

	// Check if a specific module position is completed
	const isModuleCompleted = useCallback((position: number) => {
		return state !== null && state.modulePosition >= position;
	}, [state]);

	// Lesson mode helper functions
	const isNormalMode = useCallback(() => {
		return lessonMode === "normal";
	}, [lessonMode]);

	const isBrowseMode = useCallback(() => {
		return lessonMode === "browse";
	}, [lessonMode]);

	const isReviewMode = useCallback(() => {
		return lessonMode === "review";
	}, [lessonMode]);

	const shouldSaveProgress = useCallback(() => {
		// Only save progress in normal mode
		// Browse and review modes typically don't record progress
		return isNormalMode();
	}, [isNormalMode]);

	
	// Save current state to suspend data
	const saveSuspendData = useCallback(async () => {
		if (!state) {
			return;
		}
		
		// Check if we should save progress based on lesson mode
		if (!shouldSaveProgress()) {
			return;
		}
		
		// Validate bookmark data before saving
		if (isNaN(state.modulePosition) || isNaN(state.timestamp)) {
			console.error('Invalid state data for suspend data:', state);
			return;
		}
		
		try {
			const suspendString = serializeState(state);
			
			// Only save if we got a valid CSV string
			if (suspendString) {
				setSuspendData(suspendString);
			}
		} catch (error) {
			console.error('Failed to save suspend data:', error);
		}
	}, [state, serializeState, shouldSaveProgress, lessonMode]);

	// Load state from suspend data
	const loadSuspendData = useCallback(async () => {
		try {
			const suspendString = getSuspendData();
			
			if (!suspendString || suspendString.trim() === '') {
				return;
			}

			const suspendData = deserializeState(suspendString);
			if (suspendData) {
				setState(suspendData);
			}
		} catch (error) {
			console.error('Error loading suspend data:', error);
		}
	}, [deserializeState]);

	// Load lesson mode from RXD API
	const loadLessonMode = useCallback(async () => {
		try {
			const available = isRXDAvailable();
			setRxdAvailable(available);
			
			if (!available) {
				return;
			}

			const mode = getLessonMode();
			setLessonMode(mode);
		} catch (error) {
			console.error('Error loading lesson mode:', error);
		}
	}, []);

	// Update module position and save to suspend data
	const updateModulePosition = useCallback(async (newPosition: number) => {
		if (!shouldSaveProgress()) {
			return;
		}

		try {
			const newState: RXDState = {
				modulePosition: newPosition,
				timestamp: 0 // Video time progress - 0 since we're tracking module completion, not video progress
			};
			
			// Update state and save immediately to prevent race conditions
			setState(newState);
			
			// Save directly without waiting for state update
			const suspendString = serializeState(newState);
			setSuspendData(suspendString);
		} catch (error) {
			console.error('Error updating module position:', error);
		}
	}, [shouldSaveProgress, lessonMode, serializeState]);

	// Update video progress (timestamp) without changing module position
	const updateVideoProgress = useCallback(async (timestamp: number) => {
		if (!shouldSaveProgress() || !state) {
			return;
		}

		try {
			const newState: RXDState = {
				modulePosition: state.modulePosition, // Keep current module position
				timestamp: Math.floor(timestamp) // Update video time progress
			};
			
			setState(newState);
			await saveSuspendData();
		} catch (error) {
			console.error('Error updating video progress:', error);
		}
	}, [shouldSaveProgress, state, saveSuspendData]);

	

	// Load lesson mode on mount
	useEffect(() => {
		loadLessonMode();
	}, [loadLessonMode]);

	// Save suspend data on page unload/course exit
	useEffect(() => {
		const handleBeforeUnload = () => {
			saveSuspendData();
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [saveSuspendData]);

	const contextValue: RXDStateContextType = {
		// State
		isLoading,
		error,
		rxdState: state,
		lessonMode,
		isRXDAvailable: rxdAvailable,
		
		// Actions
		// Suspend data actions
		saveSuspendData,
		loadSuspendData,
		loadLessonMode,
		updateModulePosition,
		updateVideoProgress,
		serializeState,
		deserializeState,

		// Computed values
		getNextModulePosition,
		isModulePositionAccessible,
		isModuleCompleted,
		
		// Lesson mode helpers
		isNormalMode,
		isBrowseMode,
		isReviewMode,
		shouldSaveProgress,
		
		// Utility functions
		clearError: () => setError(null),
		reset: () => {
			setState(null);
			setError(null);
			setLessonMode(null);
		},
		
		
	};

	return (
		<RXDStateContext.Provider value={contextValue}>
			{children}
		</RXDStateContext.Provider>
	);
};
